// src/components/CafeMap.js
import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import { app } from '../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Player } from '@lottiefiles/react-lottie-player';
import ReactDOM from 'react-dom';
import '../Map.css';
import animation1 from '../lotties/lottie_walkingcoffee.json'

// Custom FontAwesome icon for cafes
const coffeeIcon = L.divIcon({
  className: 'custom-icon',
  html: '<i class="fas fa-coffee" style="font-size: 32px; color: #f48e28; text-shadow: 0px 2px 2px #CC7722;"></i>',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Lottie component for the user marker
function LottieMarker() {
  return (
    <div style={{ width: '75px', height: '75px' }}>
      <Player
        src={animation1}
        loop
        autoplay
        style={{ width: '75px', height: '75px' }}
      />
    </div>
  );
}

function CafeMap() {
  const [allCafes, setAllCafes] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const userLocationRef = useRef({ latitude: null, longitude: null });
  const markerClusterGroupRef = useRef(null);
  const userMarkerRef = useRef(null);

  // Fetch all cafe locations using the new Cloud Function
  const fetchAllCafes = async () => {
    try {
      console.log('Fetching all cafe locations');
      setLoading(true);
      setError(null);

      const functions = getFunctions(app);
      console.log('Firebase functions initialized:', functions);

      const getAllCafeLocations = httpsCallable(functions, 'getAllCafeLocations');
      console.log('getAllCafeLocations callable created');

      const result = await getAllCafeLocations();
      console.log('Raw response from getAllCafeLocations:', result);

      const cafeData = result.data;
      console.log('All Cafe Locations Data:', cafeData);

      if (!Array.isArray(cafeData)) {
        throw new Error('Cafe locations data is not an array');
      }

      if (cafeData.length === 0) {
        console.warn('Received empty cafe data array from getAllCafeLocations');
        setError('No cafes found');
        setAllCafes([]);
        return;
      }

      // Ensure coordinates are numbers
      const parsedCafes = cafeData.map(cafe => {
        const latitude = parseFloat(cafe.latitude);
        const longitude = parseFloat(cafe.longitude);
        if (isNaN(latitude) || isNaN(longitude)) {
          console.warn('Skipping cafe with invalid coordinates:', cafe);
          return null;
        }
        return { ...cafe, latitude, longitude };
      }).filter(cafe => cafe !== null);

      if (parsedCafes.length === 0) {
        console.warn('No valid cafes after parsing coordinates');
        setError('No valid cafes found after parsing');
        setAllCafes([]);
        return;
      }

      console.log('Parsed Cafes with numeric coordinates:', parsedCafes);
      setAllCafes(parsedCafes);
      updateMapMarkers(parsedCafes);
    } catch (error) {
      console.error('Error fetching all cafes:', error);
      setError(`Failed to load cafes: ${error.message}`);
      setAllCafes([]);
    } finally {
      setLoading(false);
    }
  };

  // Update map markers with clustering
  const updateMapMarkers = (cafes) => {
    if (!mapRef.current) {
      console.log('Map not initialized yet, skipping updateMapMarkers');
      return;
    }
    console.log('Updating map markers with cafes:', cafes);

    // Clear existing cafe markers
    if (markerClusterGroupRef.current) {
      console.log('Removing existing marker cluster group');
      mapRef.current.removeLayer(markerClusterGroupRef.current);
    }

    // Create a new marker cluster group with adjusted settings
    console.log('Creating new marker cluster group');
    const markerClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 120, // Increase radius to cluster over a larger area (pixels)
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 15, // Stop clustering at zoom level 15
      singleMarkerMode: false, // Don't treat single markers as clusters
      chunkedLoading: true,
      iconCreateFunction: function(cluster) {
        const childCount = cluster.getChildCount();
        if (childCount < 5) {
          // If fewer than 5 markers, don't cluster (handled by plugin)
          cluster.__parent._group.options.singleMarkerMode = true;
        }
        return L.divIcon({
          html: `<div style="background-color: #f48e28; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">${childCount}</div>`,
          className: 'marker-cluster-custom',
          iconSize: L.point(40, 40),
        });
      },
    });

    // Add cafe markers to the cluster group
    cafes.forEach(cafe => {
      console.log('Adding marker for cafe:', cafe);
      try {
        if (typeof cafe.latitude !== 'number' || typeof cafe.longitude !== 'number') {
          throw new Error('Invalid coordinates');
        }
        const marker = L.marker([cafe.latitude, cafe.longitude], { icon: coffeeIcon })
          .bindPopup(`<h3>${cafe.name}</h3>`);
        markerClusterGroup.addLayer(marker);
      } catch (error) {
        console.error('Error adding marker for cafe:', cafe, error);
      }
    });

    // Add the cluster group to the map
    console.log('Adding marker cluster group to map');
    mapRef.current.addLayer(markerClusterGroup);
    markerClusterGroupRef.current = markerClusterGroup;
  };

  // Update user marker with Lottie animation
  const updateUserMarker = (lat, lng) => {
    if (!mapRef.current) {
      console.log('Map not initialized yet, skipping updateUserMarker');
      return;
    }
    // Remove existing user marker
    if (userMarkerRef.current) {
      mapRef.current.removeLayer(userMarkerRef.current);
    }

    // Create a container for the Lottie animation
    const lottieContainer = document.createElement('div');
    lottieContainer.className = 'lottie-user-marker';
    lottieContainer.style.width = '50px';
    lottieContainer.style.height = '50px';

    // Render the LottieMarker component into the container
    ReactDOM.render(<LottieMarker />, lottieContainer);

    // Create a custom divIcon with the Lottie container
    const lottieIcon = L.divIcon({
      className: 'lottie-user-marker-wrapper',
      html: lottieContainer,
      iconSize: [50, 50], // Size of the Lottie animation
      iconAnchor: [25, 25], // Center the animation
      popupAnchor: [0, -25], // Position the popup above the marker
    });

    // Add new user marker
    const userMarker = L.marker([lat, lng], { icon: lottieIcon })
      .addTo(mapRef.current)
      .bindPopup('<h3>You are here</h3>');

    userMarkerRef.current = userMarker;
    console.log('User marker with Lottie animation updated at:', [lat, lng]);
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);

    const initializeMap = async (lat, lng) => {
      userLocationRef.current = { latitude: lat, longitude: lng };

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      const map = L.map(containerRef.current, {
        center: [lat, lng],
        zoom: 14,
      });
      mapRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      console.log('Map initialized with center:', [lat, lng], 'Zoom:', map.getZoom());

      // Add user marker with Lottie animation
      updateUserMarker(lat, lng);

      // Fetch cafes
      await fetchAllCafes();
      console.log('After fetchAllCafes, allCafes:', allCafes);
    };

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        console.log('Geolocation success:', { latitude, longitude });
        initializeMap(latitude, longitude);
      },
      error => {
        console.error('Geolocation error:', error);
        initializeMap(49.2827, -123.1207); // Fallback to Vancouver
      },
      { timeout: 10000, maximumAge: 0 }
    );

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <div className="map-block">
      <h2 className="map-header">Explore Cafes Near You</h2>
      <div
        ref={containerRef}
        id="cafe-map"
        className={`map-container ${isVisible ? 'visible' : ''}`}
      />
    </div>
  );
}

export default CafeMap;